import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";
import { useLanguage } from "@/i18n/LanguageContext";

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div data-testid="language-toggle" className={`flex border border-[#4a6cf7]/40 ${className}`}>
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 font-display text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? "bg-[#4a6cf7] text-white" : "text-slate-300 hover:text-[#9db4ff]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const LINKS = [
    { name: t("nav.home"), path: "/", key: "home" },
    { name: t("nav.about"), path: "/about", key: "about" },
    { name: t("nav.products"), path: "/products", key: "products" },
    { name: t("nav.contact"), path: "/contact", key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-all duration-300 ${
        scrolled
          ? "border-[#4a6cf7]/25 bg-[#0a192f]/95 py-2"
          : "border-[#4a6cf7]/10 bg-[#0a192f]/70 py-4"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Ore Source Mining SARLU"
            className={`w-auto rounded-full bg-white p-1 ring-2 ring-[#4a6cf7]/70 shadow-[0_0_24px_rgba(74,108,247,0.45)] transition-all duration-300 ${scrolled ? "h-11" : "h-14"}`}
          />
          <div className="leading-tight">
            <p className="font-display text-sm font-bold tracking-wide text-white sm:text-base">
              ORE SOURCE MINING
            </p>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.35em] text-[#9db4ff]">
              SARLU · Lubumbashi DRC
            </p>
          </div>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `font-display text-[13px] font-medium uppercase tracking-[0.16em] transition-colors duration-300 hover:text-[#9db4ff] ${
                  isActive ? "text-[#9db4ff]" : "text-slate-300"
                }`
              }
            >
              {l.name}
            </NavLink>
          ))}
          <LangToggle />
          <Link
            to="/contact"
            data-testid="nav-cta-quote"
            className="group flex items-center gap-2 border border-[#4a6cf7]/60 bg-gradient-to-r from-[#4a6cf7] to-[#2f49c6] px-5 py-2.5 font-display text-[13px] font-semibold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
          >
            {t("nav.cta")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-[#9db4ff]"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav-overlay"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="absolute inset-x-0 top-full border-b border-[#4a6cf7]/25 bg-[#0a192f]/98 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <NavLink
                    to={l.path}
                    data-testid={`mobile-nav-link-${l.key}`}
                    className={({ isActive }) =>
                      `block border-b border-white/5 py-4 font-display text-lg font-semibold uppercase tracking-[0.15em] ${
                        isActive ? "text-[#9db4ff]" : "text-slate-200"
                      }`
                    }
                  >
                    {l.name}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-quote"
                className="mt-5 bg-gradient-to-r from-[#4a6cf7] to-[#2f49c6] px-5 py-4 text-center font-display text-sm font-bold uppercase tracking-[0.2em] text-white"
              >
                {t("nav.mobileCta")}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
